import React, { useState, ChangeEvent } from "react";
import * as palette from "@govlaunch/palette";
import NoResultsFound from "~/components/group/NoResultsFound";
import mediaQueries from "~/components/mediaQueries";
import { Input, Button } from "@govlaunch/core";
import { Margin } from "~/components/spacings";
import ProfilePicture from "@govlaunch/profile-picture";
import Dotdotdot from "~/components/text/Dotdotdot";
import Link from "next/link";
import { IProduct, ICompany } from "~/types/types";
import AddProductsToStack from "~/components/government/AddProductsToStack";
import { Toggle } from "react-powerplug";
import { NoProductsInStack } from "~/components/government/NoDataBoxes";
import { IGovernmentQuery } from "~/lib/queries/__generated__/GovernmentQuery.generated";
import { useIntl, FormattedMessage } from "react-intl";

interface IProductListProps {
  products: (Pick<IProduct, "_id" | "slug" | "logo" | "name"> & { company: Pick<ICompany, "slug" | "name"> | null })[];
  government: NonNullable<IGovernmentQuery["government"]>;
  dataIntercomTarget?: string | null;
}

export default function ProductList({ products, government, dataIntercomTarget }: IProductListProps) {
  const intl = useIntl();
  const [filter, setFilter] = useState("");

  const filtered = products.filter(product => {
    if (!product.name || !filter) {
      return true;
    }

    return product.name.toLowerCase().includes(filter.toLowerCase());
  });

  if (products.length === 0) {
    return (
      <Toggle initial={false}>
        {({ on, toggle }) => (
          <>
            <AddProductsToStack
              government={government}
              productsInStack={products as IProduct[]}
              isOpen={on}
              toggleOpen={toggle}
            />

            <NoProductsInStack government={government} onAddProduct={toggle} />
          </>
        )}
      </Toggle>
    );
  }

  return (
    <div
      data-intercom-target={dataIntercomTarget}
      css={{
        background: "#fff",
        borderRadius: 5,
        border: `1px solid ${palette.lightestGray}`,
      }}
    >
      <div
        css={{
          padding: 16,
        }}
      >
        <Margin
          mb={16}
          css={{
            display: "flex",
          }}
        >
          <Input
            placeholder={intl.formatMessage({
              defaultMessage: 'Search for products', id: 'gUIhhI',
            })}
            value={filter}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setFilter(e.target.value)}
          />

          <Toggle>
            {({ on: isOpen, toggle }) => (
              <>
                <AddProductsToStack
                  government={government}
                  productsInStack={products as IProduct[]}
                  isOpen={isOpen}
                  toggleOpen={toggle}
                />

                {government.viewerIsMember && (
                  <Button
                    theme="link"
                    color={palette.innovatorBlue}
                    textColor={palette.innovatorBlue}
                    onClick={toggle}
                    css={{
                      color: palette.innovatorBlue,
                      marginLeft: 16,
                      textTransform: "uppercase",
                    }}
                  >
                    <FormattedMessage defaultMessage="Edit" id="wEQDC6" />
                  </Button>
                )}
              </>
            )}
          </Toggle>
        </Margin>

        <div
          css={mediaQueries({
            display: "grid",
            gridTemplateColumns: ["1fr", "1fr 1fr 1fr"],
            gridColumnGap: 32,
            gridRowGap: 32,
          })}
        >
          {filtered.length === 0 && filter.length > 0 && <NoResultsFound />}

          {filtered.map(product => (
            <div
              key={product._id}
              css={{
                display: "flex",
                overflow: "hidden",
              }}
            >
              <Link href={`/products/${product.slug}`} passHref={true}>
                <a>
                  <ProfilePicture
                    key={product._id}
                    image={product.logo}
                    name={product.name}
                    size="xsmall"
                    css={{
                      borderRadius: 6,
                      "& > img": {
                        borderRadius: 6,
                        padding: 0,
                      },
                      padding: 0,
                    }}
                  />
                </a>
              </Link>

              <div
                css={{
                  marginLeft: 12,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                <Link href={`/products/${product.slug}`} passHref={true}>
                  <a>
                    <p
                      css={{
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        margin: 0,
                        fontSize: 18,
                        fontWeight: "bold",
                        color: palette.primary,
                      }}
                    >
                      {product.name}
                    </p>
                  </a>
                </Link>

                {product.company && (
                  <Link href={`/vendors/${product.company.slug}`} passHref={true}>
                    <a
                      css={{
                        fontSize: 13,
                        color: palette.sealBlue,
                        lineHeight: "17px",
                      }}
                    >
                      <Dotdotdot clamp={1}>
                        <FormattedMessage defaultMessage="From" id="dM+p3/" /> {product.company.name}
                      </Dotdotdot>
                    </a>
                  </Link>
                )}

                <p
                  css={{
                    fontSize: 13,
                    color: palette.sealBlue,
                    lineHeight: "17px",
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
